// Claude completion abstraction.
// Routes in priority order:
//   1. Supabase Edge Function (`generate-course` / `ask-mentor`) when configured — real Claude,
//      server-side key, auth-gated, rate-limited.
//   2. VITE_ANTHROPIC_PROXY_URL — legacy proxy if set.
//   3. Local deterministic fallback — keeps the app demo-able offline.

import { fnUrl, supabase } from "../lib/supabase.js";

const PROXY_URL = import.meta.env.VITE_ANTHROPIC_PROXY_URL || "";

async function authHeaders() {
  if (!supabase) return {};
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function callEdge(name, body) {
  const url = fnUrl(name);
  if (!url) return null;
  const headers = { "content-type": "application/json", ...(await authHeaders()) };
  const res = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`edge ${name} ${res.status}: ${text}`);
  }
  return res.json();
}

export async function generateCourse(query) {
  try {
    const data = await callEdge("generate-course", { query });
    if (data) return edgeCourseToText(data);
  } catch (e) {
    console.warn("generate-course failed, falling back:", e.message);
  }
  return localCourseFallback(query);
}

export async function askMentor({ message, context }) {
  try {
    const data = await callEdge("ask-mentor", { message, context });
    if (data) return data;
  } catch (e) {
    console.warn("ask-mentor failed:", e.message);
  }
  return { reply: "Mentor is unavailable right now. Try again in a moment." };
}

// ── legacy generic completion (kept so existing ExerciseRunner short-answer works) ──
export async function claudeComplete({ messages }) {
  if (PROXY_URL) {
    const res = await fetch(PROXY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages }),
    });
    if (!res.ok) throw new Error(`Claude proxy error: ${res.status}`);
    const data = await res.json();
    return data.text || "";
  }
  return localFallback(messages);
}

// ─── helpers ────────────────────────────────────────────────────────────────

function edgeCourseToText(data) {
  if (data.source === "flagship") {
    const t = data.track;
    return JSON.stringify({
      title: t.title,
      subtitle: t.subtitle,
      level: t.level,
      minutes: t.minutes,
      lessons: t.total_lessons,
      units: [{ title: "See track in catalog", bullets: ["Flagship track", "Curated lessons"] }],
    });
  }
  if (data.source === "community_cached" || data.source === "generated") {
    const c = data.course;
    return JSON.stringify({
      title: c.title,
      subtitle: c.subtitle,
      level: c.level,
      minutes: c.minutes,
      lessons: c.lessons,
      units: c.units_spec || [],
    });
  }
  return JSON.stringify({});
}

function localCourseFallback(query) {
  const topic = query.trim();
  return JSON.stringify({
    title: topic.replace(/\b\w/g, (c) => c.toUpperCase()).slice(0, 40),
    subtitle: `A hands-on, bite-sized course on ${topic}.`,
    level: "Beginner",
    minutes: 45,
    lessons: 5,
    units: [
      { title: "Foundations", bullets: ["Core concepts", "Mental models", "Common pitfalls"] },
      { title: "Practice", bullets: ["Worked examples", "Guided exercises", "Live feedback"] },
      { title: "Mastery", bullets: ["Edge cases", "Real-world patterns", "Final challenge"] },
    ],
  });
}

function localFallback(messages) {
  const content = messages?.[messages.length - 1]?.content || "";

  if (/STRICT JSON only/i.test(content) && /curriculum designer/i.test(content)) {
    const topicMatch = content.match(/typed this topic: "([^"]+)"/i);
    return localCourseFallback(topicMatch?.[1] || "AI Fundamentals");
  }

  if (/STRICT JSON only/i.test(content) && /Grade it/i.test(content)) {
    const answer = (content.match(/LEARNER'S ANSWER:\s*"""([\s\S]*?)"""/) || [])[1] || "";
    const words = answer.trim().split(/\s+/).filter(Boolean).length;
    const hasGuess = /guess|halluc|fill|make\s*up|invent/i.test(answer);
    const hasFix = /skip|mark|flag|ask|tbd|unsupported|don'?t|explicit/i.test(answer);
    const ok = words >= 15 && hasGuess && hasFix;
    return JSON.stringify({
      correct: ok,
      score: ok ? 88 : 56,
      feedback: ok
        ? "Nice — you named both the failure mode and the specific fix."
        : "Good start. Try to mention what the model does without guidance (guessing) AND the specific fallback you'd write.",
    });
  }

  return "";
}
